<template>
  <NewBsBox
    :bs="{
      display: 'flex',
      flexDirection: vertical ? 'column' : 'row',
      ...bs,
    }"
  >
    <!-- @slot 标签头部位置。特殊 DOM 结构场景使用 -->
    <slot name="head" />
    <NewBsBox
      v-if="readonly"
      :bs="{ lineHeight: '32px', fontSize: '14px' }"
    >
      {{ selectedItem.text }}
    </NewBsBox>
    <template v-else>
      <!-- @slot 默认根据 option 配置渲染，此处可以手动设置 -->
      <slot>
        <template v-if="buttonStyle">
          <RadioButton
            v-for="item in items"
            :key="item.id"
            :value="item.value"
            :disabled="!!(disabled || item.raw?.disabled)"
            :model-value="modelValue"
            :label="item.text"
            :bs="itemBs"
            @update:modelValue="onChange(item)"
          >
            <template #default>
              <!-- @slot 自定义Item的 Label -->
              <slot
                name="item-label"
                :item="item"
              >
                {{ item.text }}
              </slot>
            </template>
          </RadioButton>
        </template>
        <template v-else>
          <beer-radio
            v-for="item in items"
            :key="item.id"
            :name="uniqueName"
            :value="item.value"
            :disabled="!!(disabled || item.raw?.disabled)"
            :model-value="modelValue"
            :label="item.text"
            :bs="itemBs"
            @update:modelValue="onChange(item)"
          >
            <template #default>
              <!-- @slot 自定义Item的 Label -->
              <slot
                name="item-label"
                :item="item"
              >
                {{ item.text }}
              </slot>
            </template>
          </beer-radio>
        </template>
      </slot>
    </template>

    <BeerButton
      v-if="showClear && selectedItem && !readonly && !disabled"
      :bs="{ mr: 0, alignSelf: 'center', minWidth: 'unset'}"
      variant="stroke"
      aria-label="clear"
      size="s"
      @click="handleClear"
    >
      清空
    </BeerButton>
    <!-- @slot 标签尾部位置。特殊 DOM 结构场景使用 -->
    <slot name="tail" />
  </NewBsBox>
</template>

<script lang="ts">
  import {
    PropType, defineComponent, computed,
  } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import { uniqueId } from 'lodash'
  import { BeerStyleObj } from '@xhs/yam-beer/core'
  import useOptions, {
    useSelectValue, ExtendItem, ItemType,
    UseOptionsParams,
    GetListFn,
    provideOptionsContext,
    PossbileValue,
  } from '@xhs/yam-beer/compose/options/useOptions'
  import BeerButton from '@xhs/yam-beer/cmp/common/Button/Button.vue'
  import BeerRadio from './Radio.vue'
  import RadioButton from './RadioButton.vue'

  /**
   * 当 Radio 选项是多个的情况下使用，可以直接使用类似 select 的体验
   */
  export default defineComponent({
    name: 'BeerRadioGroup',
    components: {
      NewBsBox,
      BeerButton,
      RadioButton,
      BeerRadio,
    },
    props: {
      /**
       * @ignore
       * 容器 BS
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 是否是垂直排列
       * @version 4.1.0
       */
      vertical: {
        type: Boolean,
        default: false,
      },
      /**
       * 固定选项列表
       */
      options: {
        type: Array as PropType<ItemType[]>,
        default: (): ItemType[] => ([]),
      },
      /**
       * 选项需要从方法中调用
       * - 配合 filterable 属性可以根据筛选值来搜索
       *
       * @see Select /beer-next/ui-blocks/data-entry/doc-singledoc
       */
      getList: {
        type: Function as PropType<GetListFn>,
        required: false,
      },
      /**
       * @ignore
       * 传给 Radio 组件的 inline 样式
       */
      itemBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 受控的值
       */
      modelValue: {
        type: [String, Number, Object, Boolean] as PropType<PossbileValue>,
      },
      /**
       * Radio group name, 表示多个 radio 在同一个输入下面
       *
       * 不传的时候可以自动生成
       */
      name: {
        type: String,
        default: '',
      },
      /**
       * 是否只读
       */
      readonly: {
        type: Boolean,
        default: false,
      },
      /**
       * Group 是否禁选
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否显示清空按钮
       */
      showClear: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否选项列表找不到 modelvalue 的时候自动生成选项
       * @version 4.1.0
       */
      addIfMissing: {
        type: Boolean,
        default: false,
      },
      /**
       * useOption 的自定义选项
       * @see Select /beer-next/ui-blocks/data-entry/doc-singledoc
       */
      optionParams: {
        type: Object as PropType<UseOptionsParams>,
        default: () => ({}),
      },
      /**
       * @type {fill|outline}
       * 使用按钮样式的 Radio
       * @version 4.1.0
       */
      buttonStyle: {
        type: String,
        required: false,
      },
      /**
       * @type {s|m|l}
       * 按钮样式的 Size
       * @version 4.1.0
       */
      size: {
        type: String,
        required: false,
      },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      const uniqueName = computed(() => props.name || uniqueId('raido-'))

      const optionsHook = useOptions({
        options: computed(() => props.options),
        getList: props.getList || undefined,
        ...props.optionParams,
        filterFn: undefined,
      })

      const {
        selectedItem,
        setSelectedItem,
      } = useSelectValue(
        optionsHook.extItems,
        computed(() => props.modelValue),
        optionsHook,
        props.addIfMissing,
      )

      const onChange = (o: ExtendItem) => {
        if (typeof o.raw !== 'string' && o.raw?.disabled) {
          return
        }
        const value = setSelectedItem(o)
        if (value !== null) {
          /**
           * 输入值触发的事件
           * 可以用 v-model 双向绑定
           *
           * @property {ItemType} value - 选项值
           */
          emit('update:modelValue', value)
        }
      }

      provideOptionsContext('radiogroup', optionsHook, {
        onChange,
        uniqueName,
        size: props.size,
        buttonStyle: props.buttonStyle,
        modelValue: computed(() => props.modelValue),
      })

      const handleClear = () => {
        setSelectedItem(null)
        emit('update:modelValue', null)
      }
      return {
        uniqueName,
        selectedItem,
        items: optionsHook.items,
        onChange,
        handleClear,
      }
    },
  })
</script>
